import { z } from "zod";
import { useEffect } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { HttpError, useCreate, useOne } from "@refinedev/core";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Check,
  GripVertical,
  Info,
  Layers,
  Package,
  Plus,
  X,
} from "lucide-react";
import Input from "components/input";
import Button from "components/button";
import { Link, useNavigate } from "react-router-dom";
import { FullScreenLoading } from "components/fullScreenLoading";
import toast from "react-hot-toast";
import Select from "components/select";
import { get } from "lodash";
import { endpointService } from "components/shared/services/endpointService";
import { SwitchButton } from "components/switchButton";
import { formattedFilterParamsString } from "providerrs/data";
import { Label } from "components/label";

type AddonType = "simple" | "options";
type QtyMode = "single" | "multi_free" | "multi_fixed" | "total_cap";
type AddonScope = "event" | "ticket";
type AddonStatus = "active" | "inactive";

interface SelectOption {
  label: string;
  value: string;
}

interface IAddonVariant {
  label_vi: string;
  label_en: string;
  price: number;
  stock: number;
  qty_min: number;
  qty_max: number;
  position: number;
}

interface ICreateAddonForm {
  event_id: string;
  name_vi: string;
  name_en: string;
  desc_vi: string;
  desc_en: string;
  type: AddonType;
  status: AddonStatus;
  scope: AddonScope;
  ticket_ids: string[];
  base_price: number;
  stock_total: number | null;
  required: boolean;
  is_sold_out: boolean;
  qty_mode: QtyMode;
  qty_min: number;
  qty_max: number;
  qty_total_cap: number;
  has_note: boolean;
  note_label_vi: string;
  note_label_en: string;
  note_placeholder_vi: string;
  note_placeholder_en: string;
  note_required: boolean;
  low_stock_threshold: number;
  variants: IAddonVariant[];
  position: number;
}

const numberField = (message: string, min: number) =>
  z
    .number({
      invalid_type_error: "This field is required",
      required_error: "This field is required",
    })
    .min(min, message);

const parseNullableNumber = (value: unknown) => {
  if (value === "" || value === null || value === undefined) return null;
  if (typeof value === "string" && value.trim() === "") return null;

  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? null : numberValue;
};

const nullableNumberField = (message: string, min: number) =>
  z.preprocess(
    parseNullableNumber,
    z.number().min(min, message).nullable(),
  );

const noteRequiredFields = [
  { path: "note_label_vi", label: "Note label (vi)" },
  { path: "note_label_en", label: "Note label (en)" },
  { path: "note_placeholder_vi", label: "Placeholder (vi)" },
  { path: "note_placeholder_en", label: "Placeholder (en)" },
] as const;

const addonSchema = z
  .object({
    event_id: z.string().min(1, "This field is required"),
    name_vi: z.string().min(1, "This field is required"),
    name_en: z.string().min(1, "This field is required"),
    desc_vi: z.string().min(1, "This field is required"),
    desc_en: z.string().min(1, "This field is required"),
    type: z.enum(["simple", "options"]),
    status: z.enum(["active", "inactive"]),
    scope: z.enum(["event", "ticket"]),
    ticket_ids: z.array(z.string()).default([]),
    base_price: numberField("Base price must be 0 or greater", 0),
    stock_total: nullableNumberField("Stock must be 0 or greater", 0),
    required: z.boolean().default(false),
    is_sold_out: z.boolean().default(false),
    qty_mode: z.enum(["single", "multi_free", "multi_fixed", "total_cap"]),
    qty_min: numberField("Minimum quantity must be 0 or greater", 0).optional(),
    qty_max: numberField("Maximum quantity must be at least 1", 1).optional(),
    qty_total_cap: numberField(
      "Total quantity cap must be 0 or greater",
      0,
    ).optional(),
    has_note: z.boolean().default(false),
    note_label_vi: z.string().optional(),
    note_label_en: z.string().optional(),
    note_placeholder_vi: z.string().optional(),
    note_placeholder_en: z.string().optional(),
    note_required: z.boolean().default(false),
    low_stock_threshold: numberField(
      "Low stock threshold must be 0 or greater",
      0,
    ),
    variants: z.array(
      z.object({
        label_vi: z.string().min(1, "Variant label (vi) is required"),
        label_en: z.string().min(1, "Variant label (en) is required"),
        price: numberField("Price must be 0 or greater", 0),
        stock: numberField("Stock must be 0 or greater", 0),
        qty_min: numberField("Minimum quantity must be 0 or greater", 0).optional(),
        qty_max: numberField("Maximum quantity must be at least 1", 1).optional(),
        position: numberField("Position must be at least 1", 1),
      }),
    ),
    position: numberField("Position must be at least 1", 1),
  })
  .superRefine((values, ctx) => {
    if (values.scope === "ticket" && values.ticket_ids.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Tickets are required when scope is ticket",
        path: ["ticket_ids"],
      });
    }

    if (values.type === "options" && values.variants.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Options addon requires at least one variant",
        path: ["variants"],
      });
    }

    if (values.has_note) {
      noteRequiredFields.forEach((field) => {
        if (!values[field.path]?.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `${field.label} is required`,
            path: [field.path],
          });
        }
      });
    }

    if (values.qty_mode === "multi_fixed") {
      if (values.qty_min === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Minimum quantity is required",
          path: ["qty_min"],
        });
      }

      if (values.qty_max === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Maximum quantity is required",
          path: ["qty_max"],
        });
      }

      if (
        values.qty_min !== undefined &&
        values.qty_max !== undefined &&
        values.qty_max < values.qty_min
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            "Maximum quantity must be greater than or equal to minimum quantity",
          path: ["qty_max"],
        });
      }

      values.variants.forEach((variant, index) => {
        if (variant.qty_min === undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Qty min is required",
            path: ["variants", index, "qty_min"],
          });
        }

        if (variant.qty_max === undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Qty max is required",
            path: ["variants", index, "qty_max"],
          });
        }

        if (
          variant.qty_min !== undefined &&
          variant.qty_max !== undefined &&
          variant.qty_max < variant.qty_min
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Qty max must be greater than or equal to qty min",
            path: ["variants", index, "qty_max"],
          });
        }
      });
    }

    if (values.qty_mode === "total_cap" && values.qty_total_cap === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Total quantity cap is required",
        path: ["qty_total_cap"],
      });
    }
  });

const STATUS_OPTIONS = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const TYPE_META: Record<
  AddonType,
  {
    name: string;
    desc: string;
    bgClass: string;
    fgClass: string;
    icon: JSX.Element;
  }
> = {
  simple: {
    name: "Simple",
    desc: "One fixed price, no option choices.",
    bgClass: "bg-blue-50",
    fgClass: "text-blue-700",
    icon: <Package size={15} />,
  },
  options: {
    name: "Options",
    desc: "Multiple choices with their own price, stock, and quantity rules.",
    bgClass: "bg-green-50",
    fgClass: "text-green-700",
    icon: <Layers size={15} />,
  },
};

const QTY_MODES: Record<QtyMode, { label: string; info: string }> = {
  single: {
    label: "Choose 1",
    info: "Customer chooses one option. This is best for size, color, or a single required choice.",
  },
  multi_free: {
    label: "Free qty",
    info: "Customer can enter quantities for options without extra quantity limit fields.",
  },
  multi_fixed: {
    label: "Fixed qty",
    info: "Admin controls min and max quantity limits for this add-on and each option.",
  },
  total_cap: {
    label: "Total cap",
    info: "Customer can split quantity across options while staying under the global cap.",
  },
};

const ErrorMessage = ({
  error,
  className = "",
}: {
  error: any;
  className?: string;
}) =>
  error?.message ? (
    <p className={`text-[11.5px] text-red-500 mt-1 ${className}`}>
      <span className="font-medium">{error.message}</span>
    </p>
  ) : null;

const SectionCard = ({
  title,
  sub,
  children,
  action,
}: {
  title: string;
  sub?: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) => (
  <section className="rounded-xl border border-black/10 bg-white">
    <div className="flex items-center justify-between rounded-t-xl border-b border-black/[0.08] bg-stone-50 px-4 py-3">
      <div>
        <p className="text-[13px] font-semibold text-stone-900">{title}</p>
        {sub ? (
          <p className="mt-0.5 text-[11.5px] text-stone-500">{sub}</p>
        ) : null}
      </div>
      {action}
    </div>
    <div className="p-4">{children}</div>
  </section>
);

const Hint = ({ children }: { children: React.ReactNode }) => (
  <p className="mt-1 text-[11px] leading-relaxed text-stone-400">{children}</p>
);

const getTicketLabel = (ticket: any) =>
  ticket?.name_en ||
  ticket?.name ||
  ticket?.category?.en ||
  ticket?.category?.vi ||
  ticket?.type_name?.en ||
  ticket?.type_name?.vi ||
  ticket?.id;

const getTicketOptions = (ticketData: any): SelectOption[] => {
  const tickets = [
    ...(ticketData?.tickets ?? []),
    ...(ticketData?.group_tickets ?? []),
  ];

  return tickets.flatMap((item: any) => {
    const options: SelectOption[] = [];

    if (item.ticket?.id) {
      options.push({
        value: item.ticket.id,
        label: getTicketLabel(item.ticket),
      });
    }

    options.push(
      ...(item.sub_tickets ?? []).map((ticket: any) => ({
        value: ticket.id,
        label: getTicketLabel(ticket),
      })),
    );

    return options;
  });
};

const sanitizeVariantsForSubmit = (
  variants: IAddonVariant[],
  qtyMode: QtyMode,
) =>
  variants.map(({ qty_min, qty_max, ...variant }) =>
    qtyMode === "multi_fixed" ? { ...variant, qty_min, qty_max } : variant,
  );

const sanitizeAddonForSubmit = (formValues: ICreateAddonForm) => {
  const { qty_min, qty_max, qty_total_cap, variants, ...payload } = formValues;

  return {
    ...payload,
    stock_total: parseNullableNumber(formValues.stock_total),
    ...(formValues.qty_mode === "multi_fixed" ? { qty_min, qty_max } : {}),
    ...(formValues.qty_mode === "total_cap" ? { qty_total_cap } : {}),
    variants:
      formValues.type === "options"
        ? sanitizeVariantsForSubmit(variants, formValues.qty_mode)
        : [],
  };
};

const VariantSection = ({
  control,
  register,
  errors,
  qtyMode,
  defaultPrice,
}: {
  control: any;
  register: any;
  errors: any;
  qtyMode: QtyMode;
  defaultPrice: number;
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: "variants",
  });
  const showQtyMin = qtyMode === "multi_fixed";
  const showQtyMax = qtyMode === "multi_fixed";
  const columns = [
    { key: "drag", label: "", width: "24px" },
    { key: "label_vi", label: "Label vi", width: "minmax(150px, 1fr)" },
    { key: "label_en", label: "Label en", width: "minmax(150px, 1fr)" },
    { key: "price", label: "Price", width: "100px" },
    { key: "stock", label: "Stock", width: "86px" },
    { key: "position", label: "Position", width: "92px" },
    ...(showQtyMin
      ? [{ key: "qty_min", label: "Qty min", width: "78px" }]
      : []),
    ...(showQtyMax
      ? [{ key: "qty_max", label: "Qty max", width: "78px" }]
      : []),
    { key: "delete", label: "", width: "32px" },
  ];
  const gridTemplateColumns = columns.map((column) => column.width).join(" ");

  return (
    <SectionCard
      title={`Options (${fields.length})`}
      sub="Each option can have its own price, stock, and quantity limits."
      action={
        <button
          type="button"
          onClick={() =>
            append({
              label_vi: "",
              label_en: "",
              price: defaultPrice || 0,
              stock: 0,
              qty_min: 0,
              qty_max: 1,
              position: fields.length + 1,
            })
          }
          className="flex items-center gap-1 rounded-md border border-black/20 bg-white px-3 py-1.5 text-[12px] font-medium transition-colors hover:bg-stone-50"
        >
          <Plus size={12} /> Add
        </button>
      }
    >
      {get(errors, "variants.message") ? (
        <p className="mb-3 text-[11.5px] text-red-500">
          {get(errors, "variants.message")}
        </p>
      ) : null}

      {fields.length === 0 ? (
        <div className="rounded-lg border-[1.5px] border-dashed border-black/20 py-6 text-center text-[13px] text-stone-400">
          No options yet. Click Add to start.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <div className="min-w-[880px]">
            <div
              className="mb-1.5 grid gap-1.5 px-1"
              style={{ gridTemplateColumns }}
            >
              {columns.map((column) => (
                <span
                  key={column.key}
                  className="text-[10px] font-bold uppercase tracking-widest text-stone-400"
                >
                  {column.label}
                </span>
              ))}
            </div>
            <div className="flex flex-col gap-1.5">
              {fields.map((field, index) => {
                const fieldError = get(errors, `variants.${index}`);
                return (
                  <div key={field.id}>
                    <div
                      className="grid items-center gap-1.5 rounded-lg border border-black/10 bg-stone-50 px-2 py-2"
                      style={{ gridTemplateColumns }}
                    >
                      <div className="flex items-center justify-center text-stone-300">
                        <GripVertical size={14} />
                      </div>
                      <Input
                        placeholder="Label vi"
                        error={fieldError?.label_vi}
                        className="rounded border-black/15 bg-white px-2 py-1.5 text-[12.5px]"
                        {...register(`variants.${index}.label_vi`)}
                      />
                      <Input
                        placeholder="Label en"
                        error={fieldError?.label_en}
                        className="rounded border-black/15 bg-white px-2 py-1.5 text-[12.5px]"
                        {...register(`variants.${index}.label_en`)}
                      />
                      <Input
                        type="number"
                        placeholder="Price"
                        min={0}
                        error={fieldError?.price}
                        className="rounded border-black/15 bg-white px-2 py-1.5 text-[12.5px]"
                        {...register(`variants.${index}.price`, {
                          valueAsNumber: true,
                        })}
                      />
                      <Input
                        type="number"
                        placeholder="Stock"
                        min={0}
                        error={fieldError?.stock}
                        className="rounded border-black/15 bg-white px-2 py-1.5 text-[12.5px]"
                        {...register(`variants.${index}.stock`, {
                          valueAsNumber: true,
                        })}
                      />
                      <Input
                        type="number"
                        placeholder="Position"
                        min={1}
                        error={fieldError?.position}
                        className="rounded border-black/15 bg-white px-2 py-1.5 text-[12.5px]"
                        {...register(`variants.${index}.position`, {
                          valueAsNumber: true,
                        })}
                      />
                      {showQtyMin ? (
                        <Input
                          type="number"
                          placeholder="Qty min"
                          min={0}
                          error={fieldError?.qty_min}
                          className="rounded border-black/15 bg-white px-2 py-1.5 text-[12.5px]"
                          {...register(`variants.${index}.qty_min`, {
                            valueAsNumber: true,
                          })}
                        />
                      ) : null}
                      {showQtyMax ? (
                        <Input
                          type="number"
                          placeholder="Qty max"
                          min={1}
                          error={fieldError?.qty_max}
                          className="rounded border-black/15 bg-white px-2 py-1.5 text-[12.5px]"
                          {...register(`variants.${index}.qty_max`, {
                            valueAsNumber: true,
                          })}
                        />
                      ) : null}
                      <button
                        type="button"
                        onClick={() => remove(index)}
                        className="flex h-7 w-7 items-center justify-center rounded-md border border-black/15 bg-white text-red-400 transition-colors hover:bg-red-50 hover:text-red-600"
                      >
                        <X size={13} />
                      </button>
                    </div>
                    <ErrorMessage
                      error={fieldError?.label_vi || fieldError?.label_en}
                      className="pl-8"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </SectionCard>
  );
};

export const CreateEventAddonForm = () => {
  const navigate = useNavigate();
  const { mutate, isLoading: isLoading } = useCreate();
  const { data: eventDropdownData, isLoading: eventDropdownIsLoading } = useOne<
    any[],
    HttpError
  >({
    resource: "content/dropdown/events?uncheck=true",
    id: "",
    meta: {
      resource: "data",
    },
  });

  const eventOptions = (eventDropdownData?.data ?? []).map(
    (event: { id: string; name_en: string }) => ({
      value: event.id,
      label: event.name_en,
    }),
  );

  const {
    control,
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<ICreateAddonForm>({
    defaultValues: {
      event_id: "",
      name_vi: "",
      name_en: "",
      desc_vi: "",
      desc_en: "",
      type: "simple",
      status: "active",
      scope: "event",
      ticket_ids: [],
      base_price: 0,
      stock_total: null,
      required: false,
      is_sold_out: false,
      qty_mode: "single",
      qty_min: 1,
      qty_max: 1,
      qty_total_cap: 1,
      has_note: false,
      note_label_vi: "",
      note_label_en: "",
      note_placeholder_vi: "",
      note_placeholder_en: "",
      note_required: false,
      low_stock_threshold: 5,
      variants: [],
      position: 1,
    },
    resolver: zodResolver(addonSchema),
  });

  const values = watch();
  const type = values.type;
  const scope = values.scope;
  const selectedEventId = values.event_id;
  const { data: ticketData, isFetching: ticketIsLoading } = useOne<
    any,
    HttpError
  >({
    resource: selectedEventId
      ? endpointService.getEventEndpoint("getTickets", {
          eventId: selectedEventId,
        })
      : "",
    id: "",
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!selectedEventId,
    } as any,
  });
  const ticketOptions = getTicketOptions(ticketData?.data);

  useEffect(() => {
    setValue("ticket_ids", []);
  }, [selectedEventId, setValue]);

  const onSubmit = (formValues: ICreateAddonForm) => {
    const payload = sanitizeAddonForSubmit(formValues);

    mutate(
      {
        resource: "ws-event-addons/",
        meta: {
          ignoreResourceWrapper: true,
        },
        values: {
          ...payload,
          event_id: payload.event_id,
          ticket_ids:
            formValues.scope === "ticket" ? formValues.ticket_ids : [],
        },
      },
      {
        onSuccess: () => {
          toast.success("Create successfully");
          navigate(
            `/events/addons?${formattedFilterParamsString([
              {
                field: "event_id",
                operator: "eq",
                value: formValues.event_id,
              },
            ])}`,
          );
        },
        onError: (error: any) => {
          console.error(
            error?.response?.data?.message || "Failed to create addon",
          );
        },
      },
    );
  };

  const handleTypeChange = (value: AddonType) => {
    setValue("type", value, { shouldValidate: true });
    if (value === "simple") {
      setValue("variants", []);
      setValue("qty_mode", "single");
      setValue("qty_min", 1);
      setValue("qty_max", 1);
      setValue("qty_total_cap", 1);
    }
  };

  const handleScopeChange = (value: AddonScope) => {
    setValue("scope", value, { shouldValidate: true });
    if (value === "event") setValue("ticket_ids", []);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full flex flex-col">
      <main className="flex w-full flex-col gap-4">
        <SectionCard
          title="Context"
          sub="Choose the event before selecting ticket-level scope."
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label id="event_id" name="Event" required />
              <Controller
                control={control}
                name="event_id"
                render={({ field: { onChange, value } }) => (
                  <Select
                    id="event_id"
                    placeholder="Event"
                    options={eventOptions}
                    isLoading={eventDropdownIsLoading}
                    value={
                      eventOptions.find((opt) => opt.value === value) || null
                    }
                    onChange={(
                      selected: { label: string; value: string } | null,
                    ) => onChange(selected?.value || "")}
                    error={errors.event_id}
                  />
                )}
              />
              <ErrorMessage error={errors.event_id} />
            </div>
            <div>
              <Label id="status" name="Status" />
              <Controller
                control={control}
                name="status"
                render={({ field: { onChange, value } }) => (
                  <Select
                    id="status"
                    options={STATUS_OPTIONS}
                    value={STATUS_OPTIONS.find((opt) => opt.value === value)}
                    onChange={(
                      selected: { label: string; value: AddonStatus } | null,
                    ) => onChange(selected?.value || "active")}
                    error={errors.status}
                  />
                )}
              />
              <ErrorMessage error={errors.status} />
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Basic information">
          <div className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label id="name_vi" name="Name (vi)" required />
                <Input
                  id="name_vi"
                  placeholder="Name (vi)"
                  error={errors.name_vi}
                  {...register("name_vi")}
                />
                <ErrorMessage error={errors.name_vi} />
              </div>
              <div>
                <Label id="name_en" name="Name (en)" required />
                <Input
                  id="name_en"
                  placeholder="Name (en)"
                  error={errors.name_en}
                  {...register("name_en")}
                />
                <ErrorMessage error={errors.name_en} />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label id="desc_vi" name="Description (vi)" required />
                <Input
                  id="desc_vi"
                  placeholder="Description (vi)"
                  error={errors.desc_vi}
                  {...register("desc_vi")}
                />
                <ErrorMessage error={errors.desc_vi} />
              </div>
              <div>
                <Label id="desc_en" name="Description (en)" required />
                <Input
                  id="desc_en"
                  placeholder="Description (en)"
                  error={errors.desc_en}
                  {...register("desc_en")}
                />
                <ErrorMessage error={errors.desc_en} />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label id="base_price" name="Base price" required />
                <Input
                  id="base_price"
                  type="number"
                  placeholder="Base price"
                  min={0}
                  error={errors.base_price}
                  {...register("base_price", { valueAsNumber: true })}
                />
                {type === "options" ? (
                  <Hint>Default price for newly added options.</Hint>
                ) : null}
                <ErrorMessage error={errors.base_price} />
              </div>
              <div>
                <Label id="stock_total" name="Total stock" />
                <Controller
                  control={control}
                  name="stock_total"
                  render={({ field: { onChange, value, ref } }) => (
                    <Input
                      id="stock_total"
                      type="number"
                      placeholder="Unlimited"
                      min={0}
                      value={value ?? ""}
                      error={errors.stock_total}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        onChange(parseNullableNumber(event.target.value))
                      }
                      ref={ref}
                    />
                  )}
                />
                <Hint>Leave empty for unlimited stock.</Hint>
                {type === "options" ? (
                  <Hint>
                    Total stock for the add-on. Each option has its own stock
                    too.
                  </Hint>
                ) : null}
                <ErrorMessage error={errors.stock_total} />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label id="low_stock_threshold" name="Low stock threshold" />
                <Input
                  id="low_stock_threshold"
                  type="number"
                  placeholder="Low stock threshold"
                  min={0}
                  error={errors.low_stock_threshold}
                  {...register("low_stock_threshold", { valueAsNumber: true })}
                />
                <ErrorMessage error={errors.low_stock_threshold} />
              </div>
              <div>
                <Label id="position" name="Display position" />
                <Input
                  id="position"
                  type="number"
                  placeholder="Display position"
                  min={1}
                  error={errors.position}
                  {...register("position", { valueAsNumber: true })}
                />
                <Hint>Lower numbers appear first.</Hint>
                <ErrorMessage error={errors.position} />
              </div>
              <div>
                <Label id="required" name="Required" />
                <div className="flex min-h-[38px] items-center">
                  <SwitchButton
                    checked={values.required}
                    onChange={(checked) => setValue("required", checked)}
                  />
                </div>
                <Hint>Customer must choose this add-on.</Hint>
              </div>
              <div>
                <Label id="is_sold_out" name="Sold out" />
                <div className="flex min-h-[38px] items-center">
                  <SwitchButton
                    checked={values.is_sold_out}
                    onChange={(checked) => setValue("is_sold_out", checked)}
                  />
                </div>
                <Hint>Mark this add-on as sold out.</Hint>
              </div>
            </div>
          </div>
        </SectionCard>

        <SectionCard
          title="Addon type"
          sub="Define how customers interact with this add-on."
        >
          <div className="grid gap-2 sm:grid-cols-2">
            {(
              Object.entries(TYPE_META) as [
                AddonType,
                (typeof TYPE_META)[AddonType],
              ][]
            ).map(([key, meta]) => (
              <button
                key={key}
                type="button"
                onClick={() => handleTypeChange(key)}
                className={`rounded-lg border-[1.5px] p-3 text-left transition-all duration-100 ${
                  type === key
                    ? "border-stone-900 shadow-[0_0_0_2px_rgba(0,0,0,0.06)]"
                    : "border-black/12 hover:border-black/25 hover:bg-stone-50"
                }`}
              >
                <div
                  className={`mb-2 flex h-8 w-8 items-center justify-center rounded-md ${meta.bgClass} ${meta.fgClass}`}
                >
                  {meta.icon}
                </div>
                <p className="mb-0.5 text-[13px] font-semibold text-stone-900">
                  {meta.name}
                </p>
                <p className="text-[11.5px] leading-relaxed text-stone-500">
                  {meta.desc}
                </p>
                <div
                  className={`ml-auto mt-2 flex h-4 w-4 items-center justify-center rounded-full border-[1.5px] transition-all ${
                    type === key
                      ? "border-stone-900 bg-stone-900"
                      : "border-black/25"
                  }`}
                >
                  {type === key ? (
                    <Check size={8} color="white" strokeWidth={3} />
                  ) : null}
                </div>
              </button>
            ))}
          </div>
        </SectionCard>

        {type === "options" ? (
          <>
            <VariantSection
              control={control}
              register={register}
              errors={errors}
              qtyMode={values.qty_mode}
              defaultPrice={values.base_price}
            />

            <SectionCard
              title="Qty mode"
              sub="Control how customers choose quantities."
            >
              <div className="mb-3 flex overflow-hidden rounded-lg border border-black/12">
                {(
                  Object.entries(QTY_MODES) as [
                    QtyMode,
                    { label: string; info: string },
                  ][]
                ).map(([key, mode], index, array) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() =>
                      setValue("qty_mode", key, { shouldValidate: true })
                    }
                    className={`flex-1 whitespace-nowrap py-[7px] text-center text-[12px] transition-all ${
                      index < array.length - 1 ? "border-r border-black/12" : ""
                    } ${
                      values.qty_mode === key
                        ? "bg-stone-900 font-medium text-white"
                        : "bg-stone-50 text-stone-500 hover:bg-white"
                    }`}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>

              <div className="mb-3 flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 px-3 py-2.5 text-[12px] text-blue-700">
                <Info size={13} className="mt-0.5 shrink-0" />
                <span>{QTY_MODES[values.qty_mode].info}</span>
              </div>

              {values.qty_mode === "multi_fixed" ? (
                <div className="grid max-w-full grid-cols-2 gap-3">
                  <div>
                    <Label id="qty_min" name="Qty min" required />
                    <Input
                      id="qty_min"
                      type="number"
                      placeholder="Qty min"
                      min={0}
                      error={errors.qty_min}
                      {...register("qty_min", { valueAsNumber: true })}
                    />
                    <ErrorMessage error={errors.qty_min} />
                  </div>
                  <div>
                    <Label id="qty_max" name="Qty max" required />
                    <Input
                      id="qty_max"
                      type="number"
                      placeholder="Qty max"
                      min={1}
                      error={errors.qty_max}
                      {...register("qty_max", { valueAsNumber: true })}
                    />
                    <ErrorMessage error={errors.qty_max} />
                  </div>
                </div>
              ) : null}

              {values.qty_mode === "total_cap" ? (
                <div className="grid max-w-full grid-cols-2 gap-3">
                  <div>
                    <Label
                      id="qty_total_cap"
                      name="Qty total cap"
                      required
                    />
                    <Input
                      id="qty_total_cap"
                      type="number"
                      placeholder="Qty total cap"
                      min={0}
                      error={errors.qty_total_cap}
                      {...register("qty_total_cap", { valueAsNumber: true })}
                    />
                    <ErrorMessage error={errors.qty_total_cap} />
                  </div>
                </div>
              ) : null}
            </SectionCard>
          </>
        ) : null}

        <section className="overflow-hidden rounded-xl border border-black/10 bg-white">
          <button
            type="button"
            onClick={() =>
              setValue("has_note", !values.has_note, { shouldValidate: true })
            }
            className="flex w-full items-center justify-between border-b border-black/[0.08] bg-stone-50 px-4 py-3 transition-colors hover:bg-stone-100"
          >
            <div className="text-left">
              <p className="text-[13px] font-semibold text-stone-900">
                Customer note
              </p>
              <p className="mt-0.5 text-[11.5px] text-stone-500">
                Ask customers for extra information when they choose this
                add-on.
              </p>
            </div>
            <SwitchButton
              checked={values.has_note}
              onChange={(checked) =>
                setValue("has_note", checked, { shouldValidate: true })
              }
            />
          </button>
          {values.has_note ? (
            <div className="space-y-3 p-4">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label id="note_label_vi" name="Note label (vi)" required />
                  <Input
                    id="note_label_vi"
                    placeholder="Note label (vi)"
                    error={errors.note_label_vi}
                    {...register("note_label_vi")}
                  />
                  <ErrorMessage error={errors.note_label_vi} />
                </div>
                <div>
                  <Label id="note_label_en" name="Note label (en)" required />
                  <Input
                    id="note_label_en"
                    placeholder="Note label (en)"
                    error={errors.note_label_en}
                    {...register("note_label_en")}
                  />
                  <ErrorMessage error={errors.note_label_en} />
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label
                    id="note_placeholder_vi"
                    name="Placeholder (vi)"
                    required
                  />
                  <Input
                    id="note_placeholder_vi"
                    placeholder="Placeholder (vi)"
                    error={errors.note_placeholder_vi}
                    {...register("note_placeholder_vi")}
                  />
                  <ErrorMessage error={errors.note_placeholder_vi} />
                </div>
                <div>
                  <Label
                    id="note_placeholder_en"
                    name="Placeholder (en)"
                    required
                  />
                  <Input
                    id="note_placeholder_en"
                    placeholder="Placeholder (en)"
                    error={errors.note_placeholder_en}
                    {...register("note_placeholder_en")}
                  />
                  <ErrorMessage error={errors.note_placeholder_en} />
                </div>
              </div>
              <div className="flex items-center justify-between pt-1">
                <div>
                  <p className="text-[13px] font-medium text-stone-900">
                    Required note
                  </p>
                  <p className="mt-0.5 text-[11.5px] text-stone-500">
                    Customer must fill this before continuing.
                  </p>
                </div>
                <SwitchButton
                  checked={values.note_required}
                  onChange={(checked) => setValue("note_required", checked)}
                />
              </div>
            </div>
          ) : null}
        </section>

        <SectionCard title="Scope">
          <div className="mb-3 flex gap-2">
            {(["event", "ticket"] as AddonScope[]).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => handleScopeChange(tab)}
                className={`rounded-full border px-4 py-1.5 text-[12px] transition-all ${
                  scope === tab
                    ? "border-stone-900 bg-stone-900 text-white"
                    : "border-black/20 text-stone-500 hover:border-black/35"
                }`}
              >
                {tab === "event" ? "All tickets" : "Specific tickets"}
              </button>
            ))}
          </div>

          {scope === "ticket" ? (
            <div>
              <Label id="ticket_ids" name="Tickets" required />
              <Controller
                control={control}
                name="ticket_ids"
                render={({ field: { onChange, value } }) => (
                  <Select
                    id="ticket_ids"
                    placeholder="Tickets"
                    options={ticketOptions}
                    isLoading={ticketIsLoading}
                    isMulti
                    value={ticketOptions.filter((opt) =>
                      (value ?? []).includes(opt.value),
                    )}
                    onChange={(selected: SelectOption[] | null) => {
                      onChange((selected ?? []).map((item) => item.value));
                    }}
                    error={errors.ticket_ids}
                  />
                )}
              />
              <ErrorMessage error={errors.ticket_ids} />
            </div>
          ) : (
            <p className="text-[12.5px] text-stone-500">
              This add-on will be available for every ticket in the selected
              event.
            </p>
          )}
        </SectionCard>
        <div className="flex items-center justify-end gap-3 rounded-xl border border-black/10 bg-white px-4 py-4">
          <Link to="/events/addons">
            <Button className="h-9 w-auto border border-black/20 bg-white px-4 text-[13px] font-medium text-stone-700 hover:bg-stone-50">
              Cancel
            </Button>
          </Link>
          <Button
            type="submit"
            className="h-9 w-auto px-5 text-[13px] font-semibold active:opacity-85 active:scale-[.98]"
          >
            Create add-on
          </Button>
        </div>
      </main>
      {isLoading ? <FullScreenLoading /> : null}
    </form>
  );
};
